import { useState, useEffect, useCallback } from 'react';
import type { Notification } from '@/types/dashboard';
import { v2Api } from '@/lib/api';
import { getWebSocketBaseUrl } from '@/lib/config';
import { getStoredUserIdFromLocalStorage } from '@/lib/storedAuthUser';
import { toast } from 'sonner';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  notificationsRequested,
  notificationsReceived,
  notificationsFailed,
  markAllNotificationsRead,
  markNotificationRead,
  markNotificationUnread,
  deleteNotificationThunk,
} from '@/store/notificationsSlice';

export interface NotificationFilters {
  search?: string;
  type?: string;
  read?: boolean | null;
  ordering?: string;
  user?: string;
  page?: number;
}

export interface PaginationInfo {
  count: number;
  next: string | null;
  previous: string | null;
}

export interface UseNotificationsReturn {
  notifications: Notification[];
  loading: boolean;
  error: string | null;
  pagination: PaginationInfo | null;
  refetch: () => Promise<void>;
  markAsRead: (notification: Notification) => Promise<void>;
  markAllAsRead: (userId?: string) => Promise<void>;
  markAsUnread: (notification: Notification) => Promise<void>;
  deleteNotification: (notification: Notification) => Promise<void>;
}

export function useNotifications(filters?: NotificationFilters): UseNotificationsReturn {
  const dispatch = useAppDispatch();
  const { items: notifications, loading, error, count, next, previous } = useAppSelector(
    (state) => state.notifications,
  );
  const pagination: PaginationInfo | null = {
    count,
    next,
    previous,
  };
  const [ws, setWs] = useState<WebSocket | null>(null);

  const fetchNotifications = useCallback(async () => {
    dispatch(notificationsRequested());
    try {
      const paged = await v2Api.dashboard.notifications.list(filters);
      dispatch(
        notificationsReceived({
          items: paged.results,
          count: paged.count ?? 0,
          next: paged.next ?? null,
          previous: paged.previous ?? null,
        }),
      );
    } catch (err) {
      console.error('Error fetching notifications:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch notifications';
      dispatch(notificationsFailed(errorMessage));
    }
  }, [
    filters?.search,
    filters?.type,
    filters?.read,
    filters?.ordering,
    filters?.user,
    filters?.page,
  ]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Subscribe to WebSocket for real-time notifications (same host as API — matches Django ASGI `ws/notifications/`)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const base = getWebSocketBaseUrl();
    if (!base) return;

    const token = window.localStorage.getItem('access_token');
    const userId = getStoredUserIdFromLocalStorage();

    const params = new URLSearchParams();
    if (userId) params.set('user', userId);
    if (token) params.set('token', token);
    const qs = params.toString();
    const wsUrl = `${base}/ws/notifications/${qs ? `?${qs}` : ''}`;

    let socket: WebSocket;
    try {
      socket = new WebSocket(wsUrl);
    } catch {
      return undefined;
    }
    setWs(socket);

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as { type?: string; message?: string };
        if (data.type === 'notification') {
          // Show a toast when a realtime notification event arrives.
          toast.info(data.message || 'New notification');
          // Refresh the list so UI badges/counters update.
          fetchNotifications().catch(() => undefined);
        }
      } catch {
        // Ignore malformed messages
      }
    };

    socket.onerror = () => {
      // Non-fatal: UI will still work via HTTP polling.
    };

    socket.onclose = () => {
      setWs(null);
    };

    return () => {
      socket.close();
    };
  }, [fetchNotifications]);

  const markAsRead = useCallback(
    async (notification: Notification) => {
      try {
        await dispatch(markNotificationRead(notification)).unwrap();
        toast.success('Notification marked as read');
      } catch (err) {
        toast.error('Failed to mark notification as read');
      }
    },
    [dispatch],
  );

  const markAllAsRead = useCallback(
    async (userId?: string) => {
      try {
        await dispatch(markAllNotificationsRead(userId)).unwrap();
        toast.success('All notifications marked as read');
      } catch (err) {
        toast.error('Failed to mark all notifications as read');
      }
    },
    [dispatch],
  );

  const markAsUnread = useCallback(
    async (notification: Notification) => {
      try {
        await dispatch(markNotificationUnread(notification)).unwrap();
        toast.success('Notification marked as unread');
      } catch (err) {
        toast.error('Failed to mark notification as unread');
      }
    },
    [dispatch],
  );

  const deleteNotification = useCallback(
    async (notification: Notification) => {
      try {
        await dispatch(deleteNotificationThunk(notification)).unwrap();
        toast.success('Notification deleted');
      } catch (err) {
        toast.error('Failed to delete notification');
      }
    },
    [dispatch],
  );

  return {
    notifications,
    loading,
    error,
    pagination,
    refetch: fetchNotifications,
    markAsRead,
    markAllAsRead,
    markAsUnread,
    deleteNotification,
  };
}
